import { useEffect, useRef, useState } from 'react';
import { CalendarPlus } from 'lucide-react';
import { fmtDateShort, isoLocalDate } from '@/lib/domain/format';
import { useReferenceData } from '@/features/pipeline/useReferenceData';
import { useCommittees, useCommitteeMutations } from './api';
import { useCommitteeSchedule } from './committeeScheduleStore';
import {
  COMMITTEE_DECISION_LABELS,
  COMMITTEE_STATUS_LABELS,
  type CommitteeDecision,
  type CommitteeListItem,
  type CommitteeType,
} from './types';

const INPUT = 'rounded border border-vega-line bg-vega-surface px-2 py-1 text-[12.5px] text-vega-ink outline-none';
const STATUS_TONE: Record<string, string> = {
  Scheduled: 'bg-vega-info-soft text-vega-info',
  Held: 'bg-vega-accent-soft text-vega-accent',
  Cancelled: 'bg-vega-bg-2 text-vega-muted-2',
  Postponed: 'bg-vega-warn-soft text-vega-warn',
};
const today = () => isoLocalDate(); // achado F7 — data local, não UTC

/** Seção de Comitês do DealDrawer (DOCUMENTACAO.md §4.6): agendar/realizar/cancelar/adiar. */
export function CommitteesSection({ dealId }: { dealId: string }) {
  const { data, isLoading } = useCommittees(dealId);
  const { create, hold, cancel, postpone } = useCommitteeMutations(dealId);
  const { data: ref } = useReferenceData();
  const users = ref?.users ?? [];
  const userName = (id: string) => users.find((u) => u.id === id)?.name ?? '—';

  const [scheduling, setScheduling] = useState(false);
  const [type, setType] = useState<CommitteeType>('IC1');
  const [date, setDate] = useState(today());
  const [participants, setParticipants] = useState<string[]>([]);

  // Abre o agendamento + rola até a seção quando o menu "Ações do Deal" pede (via store global).
  // Guardamos o último tick processado num ref (inicializado no mount) para que só incrementos REAIS
  // disparem — sem auto-abrir ao reabrir um deal com tick já > 0 de uma interação anterior da sessão.
  const sectionRef = useRef<HTMLElement>(null);
  const requestTick = useCommitteeSchedule((s) => s.requestTick);
  const lastTick = useRef(requestTick);
  useEffect(() => {
    if (requestTick === lastTick.current) return;
    lastTick.current = requestTick;
    setScheduling(true);
    sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [requestTick]);

  const [action, setAction] = useState<{ id: string; kind: 'hold' | 'postpone' } | null>(null);
  const [decision, setDecision] = useState<CommitteeDecision>('Approved');
  const [minutes, setMinutes] = useState('');
  const [actionDate, setActionDate] = useState(today());

  const toggleParticipant = (id: string) =>
    setParticipants((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submitSchedule = async () => {
    await create.mutateAsync({ dealId, type, scheduledDateUtc: new Date(date + 'T12:00:00Z').toISOString(), participantUserIds: participants });
    setScheduling(false); setParticipants([]); setDate(today()); setType('IC1');
  };

  const submitAction = async (c: CommitteeListItem) => {
    if (!action) return;
    if (action.kind === 'hold') {
      await hold.mutateAsync({ id: c.id, heldDateUtc: new Date(actionDate + 'T12:00:00Z').toISOString(), decision, minutesComment: minutes || null });
    } else {
      await postpone.mutateAsync({ id: c.id, scheduledDateUtc: new Date(actionDate + 'T12:00:00Z').toISOString() });
    }
    setAction(null); setMinutes(''); setActionDate(today());
  };

  return (
    <section ref={sectionRef} className="mb-6">
      <div className="mb-2 flex items-center justify-between border-b border-vega-line pb-1.5">
        <h3 className="text-[10.5px] font-semibold uppercase tracking-wide text-vega-muted">Comitês</h3>
        <button type="button" onClick={() => setScheduling((s) => !s)} className="flex items-center gap-1 text-[11px] text-vega-ink hover:underline">
          <CalendarPlus size={13} /> Agendar
        </button>
      </div>

      {scheduling && (
        <div className="mb-3 flex flex-col gap-2 rounded border border-vega-line-2 bg-vega-surface-2 p-3">
          <div className="flex gap-2">
            <select className={INPUT} value={type} onChange={(e) => setType(e.target.value as CommitteeType)}>
              <option value="IC1">IC1</option>
              <option value="IC2">IC2</option>
            </select>
            <input type="date" className={INPUT} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {users.map((u) => (
              <label key={u.id} className={`cursor-pointer rounded border px-1.5 py-0.5 text-[11px] ${participants.includes(u.id) ? 'border-vega-ink bg-vega-bg-2 text-vega-ink' : 'border-vega-line text-vega-muted'}`}>
                <input type="checkbox" className="hidden" checked={participants.includes(u.id)} onChange={() => toggleParticipant(u.id)} />
                {u.name}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setScheduling(false)} className="text-[11px] text-vega-muted">Cancelar</button>
            <button type="button" disabled={create.isPending} onClick={() => void submitSchedule()} className="bg-vega-ink px-2.5 py-1 text-[11px] text-vega-surface disabled:opacity-40">Agendar</button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-[12.5px] text-vega-muted-2">Carregando…</p>
      ) : (data ?? []).length === 0 ? (
        <p className="text-[12.5px] text-vega-muted-2">Nenhum comitê.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {(data ?? []).map((c) => (
            <div key={c.id} className="rounded border border-vega-line-2 px-3 py-2 text-[12.5px]">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-vega-ink">{c.type}</span>
                <span className={`rounded px-1.5 py-0.5 text-[10.5px] ${STATUS_TONE[c.status] ?? 'bg-vega-bg-2 text-vega-muted-2'}`}>{COMMITTEE_STATUS_LABELS[c.status]}</span>
                {c.decision && <span className="text-[11px] text-vega-muted">· {COMMITTEE_DECISION_LABELS[c.decision]}</span>}
                <span className="flex-1" />
                <span className="font-mono text-[11px] text-vega-muted">{fmtDateShort(c.heldDateUtc ?? c.scheduledDateUtc)}</span>
              </div>
              {c.participantUserIds.length > 0 && (
                <div className="mt-1 text-[11px] text-vega-muted-2">{c.participantUserIds.map(userName).join(', ')}</div>
              )}
              {(c.status === 'Scheduled' || c.status === 'Postponed') && (
                <div className="mt-1.5 flex gap-1.5">
                  <button type="button" onClick={() => { setAction({ id: c.id, kind: 'hold' }); setActionDate(today()); }} className="rounded border border-vega-line px-2 py-0.5 text-[11px] text-vega-ink">Realizar</button>
                  <button type="button" onClick={() => { setAction({ id: c.id, kind: 'postpone' }); setActionDate(today()); }} className="rounded border border-vega-line px-2 py-0.5 text-[11px] text-vega-muted">Adiar</button>
                  <button type="button" onClick={() => void cancel.mutate({ id: c.id })} className="rounded border border-vega-line px-2 py-0.5 text-[11px] text-vega-muted-2">Cancelar</button>
                </div>
              )}
              {action?.id === c.id && (
                <div className="mt-2 flex flex-col gap-2 rounded border border-vega-line-2 bg-vega-surface-2 p-2">
                  <input type="date" className={INPUT} value={actionDate} onChange={(e) => setActionDate(e.target.value)} />
                  {action.kind === 'hold' && (
                    <>
                      <select className={INPUT} value={decision} onChange={(e) => setDecision(e.target.value as CommitteeDecision)}>
                        {(Object.keys(COMMITTEE_DECISION_LABELS) as CommitteeDecision[]).map((d) => (
                          <option key={d} value={d}>{COMMITTEE_DECISION_LABELS[d]}</option>
                        ))}
                      </select>
                      <textarea className={INPUT} rows={2} placeholder="Ata (opcional)" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
                    </>
                  )}
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setAction(null)} className="text-[11px] text-vega-muted">Fechar</button>
                    <button type="button" onClick={() => void submitAction(c)} className="bg-vega-ink px-2.5 py-1 text-[11px] text-vega-surface">Confirmar</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
